import type {
  PermissionCategory,
  PermissionNode,
  PermissionState,
  ToggleState,
} from "../../types";

// Get all keys from this node and its children
export function getAllDescendantKeys(node: PermissionNode): string[] {
  const keys: string[] = [node.key];
  if (node.items?.length) {
    for (const child of node.items) {
      keys.push(...getAllDescendantKeys(child));
    }
  }
  return keys;
}

// Get only the bottom-level keys (nodes with no children)
export function getLeafKeys(node: PermissionNode): string[] {
  if (!node.items?.length) {
    return [node.key];
  }
  const keys: string[] = [];
  for (const child of node.items) {
    keys.push(...getLeafKeys(child));
  }
  return keys;
}

// Returns "on", "off", or "indeterminate" based on how many children are active
export function getToggleState(
  node: PermissionNode,
  permissions: PermissionState,
): ToggleState {
  if (!node.items?.length) {
    return permissions[node.key] ? "on" : "off";
  }

  // Only look at leaf nodes to decide the parent's state
  const leafKeys = getLeafKeys(node);
  if (leafKeys.length === 0) {
    return permissions[node.key] ? "on" : "off";
  }
  const activeCount = leafKeys.filter((k) => permissions[k]).length;

  if (activeCount === 0) return "off";
  if (activeCount === leafKeys.length) return "on";
  return "indeterminate";
}

// Find a node by key and return it with its parent path
function findNodeWithPath(
  categories: PermissionCategory[],
  targetKey: string,
): { node: PermissionNode; path: PermissionNode[] } | null {
  for (const cat of categories) {
    const rootNode: PermissionNode = {
      key: cat.category_key,
      display_name: cat.display_name,
      items: cat.items,
    };
    const result = findInNode(rootNode, targetKey, []);
    if (result) return result;
  }
  return null;
}

function findInNode(
  node: PermissionNode,
  targetKey: string,
  parentPath: PermissionNode[],
): { node: PermissionNode; path: PermissionNode[] } | null {
  if (node.key === targetKey) {
    return { node, path: parentPath };
  }
  if (node.items?.length) {
    for (const child of node.items) {
      const result = findInNode(child, targetKey, [...parentPath, node]);
      if (result) return result;
    }
  }
  return null;
}

// Check if all siblings (except the target) are off
function areAllSiblingsOff(
  parent: PermissionNode,
  targetKey: string,
  permissions: PermissionState,
): boolean {
  if (!parent.items?.length) return true;
  for (const sibling of parent.items) {
    if (sibling.key === targetKey) continue;
    const siblingKeys = getAllDescendantKeys(sibling);
    if (siblingKeys.some((k) => permissions[k])) return false;
  }
  return true;
}

// Toggle a node and cascade: ON turns on children + parents, OFF turns off children + parents (if no siblings are on)
export function computeCascadeToggle(
  categories: PermissionCategory[],
  toggledKey: string,
  newValue: boolean,
  currentPermissions: PermissionState,
): PermissionState {
  const updated = { ...currentPermissions };
  const found = findNodeWithPath(categories, toggledKey);

  if (!found) {
    // Key not in tree, just toggle it
    updated[toggledKey] = newValue;
    return updated;
  }

  const { node, path } = found;

  if (newValue) {
    // Turn on this node + all children
    const allKeys = getAllDescendantKeys(node);
    for (const key of allKeys) {
      updated[key] = true;
    }
    // Also turn on all parents
    for (const ancestor of path) {
      updated[ancestor.key] = true;
    }
  } else {
    // Turn off this node + all children
    const allKeys = getAllDescendantKeys(node);
    for (const key of allKeys) {
      updated[key] = false;
    }
    // Turn off parents too, but only if all their other children are also off
    for (let i = path.length - 1; i >= 0; i--) {
      const ancestor = path[i];
      if (areAllSiblingsOff(ancestor, i === path.length - 1 ? toggledKey : path[i + 1].key, updated)) {
        updated[ancestor.key] = false;
      } else {
        // This parent stays on, so all grandparents stay on too
        break;
      }
    }
  }

  return updated;
}

// Remove nodes matching the given keys from the tree
export function filterCategories(
  categories: PermissionCategory[],
  filterKeys: string[],
): PermissionCategory[] {
  if (!filterKeys.length) return categories;
  const filterSet = new Set(filterKeys);

  function filterNode(node: PermissionNode): PermissionNode | null {
    if (filterSet.has(node.key)) return null;
    if (!node.items?.length) return node;
    const filteredItems = node.items
      .map(filterNode)
      .filter((n): n is PermissionNode => n !== null);
    return { ...node, items: filteredItems };
  }

  return categories
    .map((cat) => {
      if (filterSet.has(cat.category_key)) return null;
      const filteredItems = (cat.items ?? [])
        .map(filterNode)
        .filter((n): n is PermissionNode => n !== null);
      return { ...cat, items: filteredItems };
    })
    .filter((cat): cat is NonNullable<typeof cat> => cat !== null) as PermissionCategory[];
}
